import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Collapse,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Search,
  XCircle,
  XOctagon,
} from "react-feather";
import { useSearchParams } from "react-router";
import { useGetLazy, usePost } from "../../api";
import {
  IOpenDataCheck,
  IOpenDataProductMapQuery,
  IOpenDataProductMapResult,
  IOpenDataPromo,
  IOpenDataPromoMetadata,
  IOpenDataPromoResult,
  IOpenDataSourceRef,
  IOpenDataValidationResult,
  OpenDataCheckStatus,
  OpenDataConfidence,
  OpenDataSourceKind,
} from "../../interfaces";
import { JsonBlock } from "./json-block";
import { ProductLinks } from "./product-links";

// These two panels are driven from the page rather than a check, so they name
// their own source.
const SOURCE_PROMO: IOpenDataSourceRef = {
  kind: OpenDataSourceKind.Partner,
  name: "promo-discovery",
  endpoint: "GET /v1/opendata/promo-discovery",
};
const SOURCE_PRODUCT_MAP: IOpenDataSourceRef = {
  kind: OpenDataSourceKind.Partner,
  name: "product-map",
  endpoint: "POST /v1/OpenData/product-map",
};

const HISTORY_KEY = "open_data_validation_history_v2";
const MAX_HISTORY = 10;

interface HistoryEntry {
  domain: string;
  country: string;
  /** Absent on entries saved before the language filter existed. */
  language?: string;
}

const loadHistory = (): HistoryEntry[] => {
  try {
    return JSON.parse(localStorage.getItem(HISTORY_KEY) ?? "[]");
  } catch {
    return [];
  }
};

const saveHistory = (entry: HistoryEntry): HistoryEntry[] => {
  const prev = loadHistory().filter(
    (h) =>
      !(
        h.domain === entry.domain &&
        h.country === entry.country &&
        (h.language ?? "") === (entry.language ?? "")
      )
  );
  const next = [entry, ...prev].slice(0, MAX_HISTORY);
  localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
  return next;
};

/** Mirrors the backend ordering rule so the highlight matches the sort. */
const GEO_ALIASES: Record<string, string> = { uk: "gb", gb: "uk" };

const regionMatches = (region: string | null, country: string): boolean => {
  if (!region) return false;
  const r = region.trim().toLowerCase();
  const c = country.trim().toLowerCase();
  return r === c || r === GEO_ALIASES[c];
};

const STATUS_META: Record<
  OpenDataCheckStatus,
  { variant: string; icon: React.ReactNode; label: string }
> = {
  [OpenDataCheckStatus.Pass]: {
    variant: "success",
    icon: <CheckCircle size={16} />,
    label: "Available",
  },
  [OpenDataCheckStatus.Warn]: {
    variant: "warning",
    icon: <AlertTriangle size={16} />,
    label: "Partial",
  },
  [OpenDataCheckStatus.Fail]: {
    variant: "danger",
    icon: <XCircle size={16} />,
    label: "Missing",
  },
  [OpenDataCheckStatus.Error]: {
    variant: "secondary",
    icon: <XOctagon size={16} />,
    label: "Error",
  },
};

/** Names the API behind a panel so a reviewer knows what they are looking at. */
const SourceTag: React.FC<{ source: IOpenDataSourceRef }> = ({ source }) => {
  const partner = source.kind === OpenDataSourceKind.Partner;
  return (
    <span
      className="d-inline-flex align-items-center gap-1 small"
      title={source.endpoint}
    >
      <Badge bg={partner ? "primary" : "secondary"}>
        {partner ? "Open Data API" : "Internal API"}
      </Badge>
      <code className="text-muted">{source.name}</code>
    </span>
  );
};

const CheckCard: React.FC<{ check: IOpenDataCheck }> = ({ check }) => {
  const meta = STATUS_META[check.status];
  return (
    <Col md={6} xl={3}>
      <Card className={`h-100 border-${meta.variant}`}>
        <Card.Header
          className={`small fw-bold d-flex justify-content-between align-items-center bg-${meta.variant} text-white`}
        >
          <span className="d-flex align-items-center gap-1">
            {meta.icon}
            {check.label}
          </span>
          <Badge bg="light" text="dark">
            {meta.label}
          </Badge>
        </Card.Header>
        <Card.Body className="small">
          <div className="mb-2">{check.summary}</div>
          {check.error && (
            <div className="text-danger small mb-2">{check.error}</div>
          )}
          {Object.entries(check.details).map(([k, v]) => (
            <div key={k} className="d-flex justify-content-between">
              <span className="text-muted">{k}</span>
              <span className="fw-semibold text-end ms-2">{v}</span>
            </div>
          ))}
        </Card.Body>
        <Card.Footer className="bg-white py-2">
          <SourceTag source={check.source} />
        </Card.Footer>
      </Card>
    </Col>
  );
};

const COVERAGE_VARIANT: Record<string, string> = {
  "Site-Wide": "primary",
  Category: "info",
  Product: "warning",
  "Not Set": "secondary",
};

const coverageName = (coverage: number | null | undefined): string =>
  coverage === 1
    ? "Site-Wide"
    : coverage === 2
      ? "Category"
      : coverage === 4
        ? "Product"
        : "Not Set";

const discountText = (metadata: IOpenDataPromoMetadata | null): string => {
  const d = metadata?.discounts?.[0];
  // Type 3 (free delivery, gift-with-purchase, multibuy) never carries a value.
  if (!d?.discountValue)
    return metadata?.discountType === 3
      ? (metadata.nonMonetaryValue ?? "Non-monetary offer")
      : "—";
  return metadata?.discountType === 1
    ? `${d.discountValue}% off`
    : `${d.discountValue} ${metadata?.currency ?? ""} off`.trim();
};

/** The promos behind the coupon checks, laid out so they can be skimmed. */
/**
 * Inferred from the promo descriptions the API returns, not from a published
 * schema — every "% off" carries 1 and every "£x off" carries 2.
 */
const DISCOUNT_TYPE_NAMES: Record<number, string> = {
  0: "none",
  1: "percentage",
  2: "fixed amount",
  3: "non-monetary",
};

const discountTypeName = (t: number | null | undefined): string =>
  t === null || t === undefined
    ? "—"
    : `${t} (${DISCOUNT_TYPE_NAMES[t] ?? "unknown"})`;

const enumText = (
  value: number | null | undefined,
  name?: (v: number | null | undefined) => string
): string => {
  if (value === null || value === undefined) return "—";
  return name ? `${value} (${name(value)})` : String(value);
};

const val = (v: unknown): string =>
  v === null || v === undefined || v === "" ? "—" : String(v);

const MetaRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <tr>
    <td className="text-muted py-1" style={{ width: "45%" }}>
      {label}
    </td>
    <td className="py-1 text-break">{children}</td>
  </tr>
);

const PromoCard: React.FC<{ promo: IOpenDataPromo }> = ({ promo: p }) => {
  const [open, setOpen] = useState(false);
  const coverage = coverageName(p.metadata?.coverage);
  const m = p.metadata;

  return (
    <Col md={6} xl={4}>
      <div className="border rounded p-2 h-100 small d-flex flex-column">
        <div className="d-flex justify-content-between align-items-start gap-2">
          <code className="fw-bold text-nowrap">{p.code ?? "—"}</code>
          <Badge bg={COVERAGE_VARIANT[coverage] ?? "secondary"}>{coverage}</Badge>
        </div>
        <div className="mt-1">{p.description ?? "—"}</div>
        <div className="text-muted mt-1">
          {discountText(m)}
          {m?.category ? ` · ${m.category}` : ""}
        </div>
        <div className="text-muted mt-auto pt-1">
          {p.endDate
            ? `Ends ${new Date(p.endDate).toLocaleDateString()}`
            : "No end date"}
        </div>
        {p.activationLink && (
          <a
            href={p.activationLink}
            target="_blank"
            rel="noopener noreferrer"
            className="d-inline-flex align-items-center gap-1 mt-1"
          >
            Activation link <ExternalLink size={12} />
          </a>
        )}

        <button
          type="button"
          className="btn btn-link btn-sm p-0 mt-1 text-decoration-none text-start d-inline-flex align-items-center gap-1"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
        >
          {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          {open ? "Hide metadata" : "Metadata"}
          {!m && <span className="text-danger ms-1">(none)</span>}
        </button>

        <Collapse in={open}>
          <div>
            <Table size="sm" borderless className="small mb-0 mt-1">
              <tbody>
                <MetaRow label="Coverage">
                  {enumText(m?.coverage, coverageName)}
                </MetaRow>
                <MetaRow label="Scope">{val(m?.scope)}</MetaRow>
                <MetaRow label="Discount type">
                  {discountTypeName(m?.discountType)}
                </MetaRow>
                <MetaRow label="Promo type (meta)">{val(m?.promoType)}</MetaRow>
                <MetaRow label="Category">{val(m?.category)}</MetaRow>
                <MetaRow label="Currency">{val(m?.currency)}</MetaRow>
                <MetaRow label="Language">{val(m?.language)}</MetaRow>
                <MetaRow label="Non-monetary value">
                  {val(m?.nonMonetaryValue)}
                </MetaRow>
                {(m?.discounts ?? []).map((d, i) => (
                  <MetaRow
                    key={`${d.discountValue}-${d.minimumSpend}-${i}`}
                    label={`Discount ${i + 1}`}
                  >
                    value {val(d.discountValue)} · max {val(d.maxDiscountValue)}{" "}
                    · min spend {val(d.minimumSpend)}
                  </MetaRow>
                ))}
                <MetaRow label="Promo type">{val(p.promoType)}</MetaRow>
                <MetaRow label="Brand">
                  {val(p.brandName)}
                  {p.brandId && (
                    <div className="text-muted">
                      <code>{p.brandId}</code>
                    </div>
                  )}
                </MetaRow>
                <MetaRow label="Domain">{val(p.domain)}</MetaRow>
                <MetaRow label="Priority">{val(p.priority)}</MetaRow>
                <MetaRow label="Starts">
                  {p.startDate ? new Date(p.startDate).toLocaleString() : "—"}
                </MetaRow>
                <MetaRow label="Ends">
                  {p.endDate ? new Date(p.endDate).toLocaleString() : "—"}
                </MetaRow>
                <MetaRow label="Promo link">
                  {p.promoLink ? (
                    <a href={p.promoLink} target="_blank" rel="noopener noreferrer">
                      open
                    </a>
                  ) : (
                    "—"
                  )}
                </MetaRow>
                <MetaRow label="Promo ID">
                  <code>{p.promoId}</code>
                </MetaRow>
              </tbody>
            </Table>
          </div>
        </Collapse>
      </div>
    </Col>
  );
};

const PromoList: React.FC<{ result: IOpenDataPromoResult }> = ({ result }) => {
  const [open, setOpen] = useState(true);
  return (
    <Card className="mb-4">
      <Card.Header
        role="button"
        onClick={() => setOpen((o) => !o)}
        className="fw-bold small d-flex justify-content-between align-items-center"
      >
        <span className="d-flex align-items-center gap-2">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          Promos
          <Badge bg="dark">{result.total}</Badge>
          {Object.entries(result.coverageCounts).map(([label, n]) => (
            <Badge key={label} bg={COVERAGE_VARIANT[label] ?? "secondary"}>
              {label} {n}
            </Badge>
          ))}
        </span>
        <SourceTag source={SOURCE_PROMO} />
      </Card.Header>
      <Collapse in={open}>
        <div>
          <Card.Body className="pt-3">
            {result.dealLink && (
              <div className="small text-muted mb-3">
                Brand deal link — where a promo with no activation link of its
                own sends the shopper.{" "}
                <a
                  href={result.dealLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open <ExternalLink size={12} />
                </a>
              </div>
            )}
            <Row className="g-2">
              {result.promos.map((p) => (
                <PromoCard key={p.promoId} promo={p} />
              ))}
            </Row>
          </Card.Body>
        </div>
      </Collapse>
    </Card>
  );
};

/**
 * product-map has no domain-only query, so products for a domain can only be
 * found via a keyword. Runs at zero confidence on purpose — the domain pin is
 * what makes the matches this merchant's.
 */
const ProductLookup: React.FC<{
  domain: string;
  country: string;
  language: string | null;
  initialKeyword: string;
  initialResult: IOpenDataProductMapResult | null;
}> = ({ domain, country, language, initialKeyword, initialResult }) => {
  const [keyword, setKeyword] = useState(initialKeyword);
  // Off by default so this panel keeps matching the validation run above it;
  // the validation asks whether the domain has usable product data at all.
  const [inStockOnly, setInStockOnly] = useState(false);
  const { data, loading, error, execute } = usePost<
    IOpenDataProductMapResult,
    IOpenDataProductMapQuery
  >();
  // The validation already ran this search when a keyword was given.
  const result = data ?? initialResult;

  const search = (term: string) => {
    if (!term.trim()) return;
    execute("open-data/product-map", {
      productName: term.trim(),
      domain,
      countryCode: country,
      ...(language ? { language } : {}),
      maxResultCount: 20,
      minimumConfidence: OpenDataConfidence.Any,
      ...(inStockOnly ? { inStockOnly: true } : {}),
    }).catch(() => undefined);
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    search(keyword);
  };

  // Re-run on toggle so the switch takes effect without a second click.
  const onStockToggle = (checked: boolean) => {
    setInStockOnly(checked);
    if (!keyword.trim()) return;
    execute("open-data/product-map", {
      productName: keyword.trim(),
      domain,
      countryCode: country,
      ...(language ? { language } : {}),
      maxResultCount: 20,
      minimumConfidence: OpenDataConfidence.Any,
      ...(checked ? { inStockOnly: true } : {}),
    }).catch(() => undefined);
  };

  return (
    <Card className="mb-4">
      <Card.Header className="fw-bold small d-flex justify-content-between align-items-center">
        <span>Product lookup</span>
        <SourceTag source={SOURCE_PRODUCT_MAP} />
      </Card.Header>
      <Card.Body>
        <Form onSubmit={onSubmit}>
          <Row className="g-2 align-items-end">
            <Col md={9}>
              <Form.Label className="small fw-bold">
                Keyword <span className="text-muted fw-normal">(required)</span>
              </Form.Label>
              <Form.Control
                placeholder="e.g. laptop, trainers, coffee"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
            </Col>
            <Col md={3} className="d-flex align-items-center">
              <Form.Check
                type="switch"
                id="validation-in-stock-only"
                label="In stock only"
                checked={inStockOnly}
                onChange={(e) => onStockToggle(e.target.checked)}
                disabled={loading}
                title="Off returns every match. On returns only items the feed currently reports as in stock."
              />
            </Col>
            <Col md={3}>
              <Button
                type="submit"
                variant="outline-primary"
                className="w-100"
                disabled={loading || !keyword.trim()}
              >
                {loading ? (
                  <Spinner size="sm" animation="border" />
                ) : (
                  <>
                    <Search size={16} className="me-1" /> Find products
                  </>
                )}
              </Button>
            </Col>
          </Row>
        </Form>
        <div className="small text-muted mt-2">
          The product map cannot be queried by domain alone, so this needs a
          keyword. It returns the top 20 matches at <strong>Any</strong>{" "}
          confidence — pinning the domain is what keeps them relevant, so treat
          a weak name match as “this merchant has products”, not “this is the
          right product”. <strong>In stock only</strong> narrows it to what the
          feed says is buyable right now; leave it off to judge the catalogue
          itself, which is what the validation above reports on.
        </div>

        {error && (
          <Alert variant="danger" className="mt-3 mb-0">
            {(error.data as any)?.message ?? "Product lookup failed."}
          </Alert>
        )}

        {result && (
          <div className="mt-3">
            {result.results.length === 0 ? (
              <div className="text-muted small">
                No products matched “{keyword}” on {domain}.
              </div>
            ) : (
              <>
                <div className="d-flex flex-wrap gap-2 mb-2">
                  <Badge bg="dark">{result.count} products</Badge>
                  {Object.entries(result.networkCounts).map(([net, n]) => (
                    <Badge bg="info" key={net}>
                      {net}: {n}
                    </Badge>
                  ))}
                </div>
                <Row className="g-2">
                  {result.results.map((m) => (
                    <Col md={6} xl={4} key={m.product.id}>
                      <div className="border rounded p-2 h-100 small">
                        <div className="fw-semibold">{m.product.name}</div>
                        <div className="text-muted mb-1">
                          {m.network && (
                            <Badge bg="info" className="me-1">
                              {m.network}
                            </Badge>
                          )}
                          {m.product.merchantName ?? "—"} ·{" "}
                          {Math.round(m.matchConfidence * 100)}%
                        </div>
                        <ProductLinks product={m.product} />
                      </div>
                    </Col>
                  ))}
                </Row>
              </>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export const DomainValidation: React.FC = () => {
  // The readiness list links straight here for one merchant.
  const [params] = useSearchParams();
  const [domain, setDomain] = useState(params.get("domain") ?? "");
  const [country, setCountry] = useState(
    (params.get("country") ?? "UK").toUpperCase()
  );
  const [language, setLanguage] = useState("");
  const [keyword, setKeyword] = useState("");
  const [history, setHistory] = useState<HistoryEntry[]>(loadHistory);
  const [feedOpen, setFeedOpen] = useState(false);

  const { data, loading, error, execute } =
    useGetLazy<IOpenDataValidationResult>();

  const run = (d: string, c: string, l: string, k: string) => {
    const cleanDomain = d
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, "")
      .replace(/^www\./, "")
      .replace(/\/.*$/, "");
    const cleanCountry = c.trim().toUpperCase();
    const cleanLanguage = l.trim().toLowerCase();
    const cleanKeyword = k.trim();
    if (!cleanDomain) return;
    const query = `domain=${encodeURIComponent(cleanDomain)}&country=${encodeURIComponent(cleanCountry)}${
      cleanLanguage ? `&language=${encodeURIComponent(cleanLanguage)}` : ""
    }${cleanKeyword ? `&keyword=${encodeURIComponent(cleanKeyword)}` : ""}`;
    execute(`open-data/validate?${query}`).catch(() => undefined);
    setHistory(
      saveHistory({
        domain: cleanDomain,
        country: cleanCountry,
        language: cleanLanguage || undefined,
      })
    );
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    run(domain, country, language, keyword);
  };

  // A linked-to domain validates itself once, so the page is not just a filled
  // form waiting for a click.
  const autoRan = useRef(false);
  useEffect(() => {
    const linked = params.get("domain");
    if (!linked || autoRan.current) return;
    autoRan.current = true;
    run(
      linked,
      params.get("country") ?? "UK",
      params.get("language") ?? "",
      params.get("keyword") ?? ""
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Card className="mb-4">
        <Card.Body>
          <Form onSubmit={onSubmit}>
            <Row className="align-items-end g-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label className="small fw-bold">Domain</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. currys.co.uk"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label className="small fw-bold">Country</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="UK"
                    value={country}
                    onChange={(e) => setCountry(e.target.value.toUpperCase())}
                    style={{ textTransform: "uppercase" }}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label className="small fw-bold">
                    Language{" "}
                    <span className="text-muted fw-normal">(optional)</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="en"
                    maxLength={2}
                    value={language}
                    onChange={(e) => setLanguage(e.target.value.toLowerCase())}
                    style={{ textTransform: "lowercase" }}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label className="small fw-bold">
                    Product keyword{" "}
                    <span className="text-muted fw-normal">(optional)</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. laptop"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading || !domain.trim()}
                  className="w-100"
                >
                  {loading ? (
                    <Spinner size="sm" animation="border" />
                  ) : (
                    <>
                      <Search size={16} className="me-1" /> Validate
                    </>
                  )}
                </Button>
              </Col>
            </Row>
          </Form>

          <div className="small text-muted mt-2">
            A product keyword adds a Product map check — the partner view of
            whether this merchant has products, next to our own feed — and fills
            the product lookup below with the same search.
          </div>

          {history.length > 0 && (
            <div className="mt-3 d-flex flex-wrap gap-2 align-items-center">
              <span className="small text-muted">Recent:</span>
              {history.map((h) => (
                <Button
                  key={`${h.domain}-${h.country}-${h.language ?? ""}`}
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => {
                    setDomain(h.domain);
                    setCountry(h.country);
                    setLanguage(h.language ?? "");
                    run(h.domain, h.country, h.language ?? "", keyword);
                  }}
                >
                  {h.domain} ({h.country}
                  {h.language ? `/${h.language}` : ""})
                </Button>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger">
          Validation failed. Please try again, or check the domain spelling.
        </Alert>
      )}

      {data && (
        <>
          <Alert variant="light" className="border">
            <strong>{data.domain}</strong> ({data.country}
            {data.language ? `/${data.language}` : ""}) —
            checked {new Date(data.checkedAt).toLocaleString()}
          </Alert>

          <Row className="g-3 mb-4">
            {data.checks.map((c) => (
              <CheckCard key={c.key} check={c} />
            ))}
          </Row>

          {data.feed && data.feed.advertisers.length > 0 && (
            <Card className="mb-4">
              <Card.Header
                role="button"
                onClick={() => setFeedOpen((o) => !o)}
                className="fw-bold small d-flex justify-content-between align-items-center"
              >
                <span className="d-flex align-items-center gap-2">
                  {feedOpen ? (
                    <ChevronDown size={14} />
                  ) : (
                    <ChevronRight size={14} />
                  )}
                  Product feed advertisers
                  <Badge bg="dark">{data.feed.advertisers.length}</Badge>
                  <Badge bg="secondary">
                    {data.feed.totalProducts.toLocaleString()} products
                  </Badge>
                  {data.feed.networks.map((n) => (
                    <Badge bg="info" key={n}>
                      {n}
                    </Badge>
                  ))}
                </span>
                <SourceTag source={data.feed.source} />
              </Card.Header>
              <Collapse in={feedOpen}>
                <div>
                  <div style={{ maxHeight: 360, overflowY: "auto" }}>
              <Table size="sm" className="mb-0" responsive>
                <thead>
                  <tr>
                    <th>Network</th>
                    <th>Advertiser</th>
                    <th>Domain</th>
                    <th>Region</th>
                    <th className="text-end">Products</th>
                    <th>Last discovered</th>
                  </tr>
                </thead>
                <tbody>
                  {data.feed.advertisers.map((a) => (
                    <tr
                      key={`${a.network}-${a.advertiserId}`}
                      className={
                        regionMatches(a.region, data.country)
                          ? "table-primary"
                          : undefined
                      }
                    >
                      <td>
                        <Badge bg="info">{a.network}</Badge>
                      </td>
                      <td>{a.name}</td>
                      <td className="small text-muted">{a.domain ?? "—"}</td>
                      <td className="small">
                        {a.region ? (
                          <Badge
                            bg={
                              regionMatches(a.region, data.country)
                                ? "primary"
                                : "light"
                            }
                            text={
                              regionMatches(a.region, data.country)
                                ? undefined
                                : "dark"
                            }
                          >
                            {a.region.toUpperCase()}
                          </Badge>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="text-end fw-semibold">
                        {a.productCount.toLocaleString()}
                      </td>
                      <td className="small text-muted">
                        {a.lastDiscoveredAt
                          ? new Date(a.lastDiscoveredAt).toLocaleDateString()
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
                  </div>
                </div>
              </Collapse>
            </Card>
          )}

          {data.promos && data.promos.promos.length > 0 && (
            <PromoList result={data.promos} />
          )}

          <ProductLookup
            key={data.checkedAt}
            domain={data.domain}
            country={data.country}
            language={data.language}
            initialKeyword={data.keyword ?? ""}
            initialResult={data.productMap}
          />

          <JsonBlock value={data} title="Raw validation payload" />
        </>
      )}
    </div>
  );
};
